import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { trim, remove, toLower, find } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class LampsSelectedService {
  public isProductDetailMode = false;
  public lampRemoved = new Subject<any>();
  public lampAdded = new Subject<any>();
  public excludedLuminaireForms = ['anstrahlung', 'decken', 'poller', 'system', 'verkehrszeichen'];

  private dataStore: any = {
    lamps: [],
    productDetailLamps: [],
  };

  private lamps$ = new BehaviorSubject([]);

  public isExcludedFromPlanning(luminaire: any) {
    return this.excludedLuminaireForms.indexOf(trim(toLower(luminaire.luminaireForm))) >= 0;
  }

  public get lamps(): Observable<any[]> {
    return this.lamps$.asObservable();
  }

  public get productDetailLamps(): any[] {
    return this.dataStore.productDetailLamps;
  }

  public get selectedLampsLength(): number {
    return this.dataStore.lamps.length;
  }

  public addLamp(lamp: any) {
    lamp.selected = true;

    this.dataStore.lamps.push(lamp);

    this.lampAdded.next(lamp);

    this.lamps$.next(Object.assign({}, this.dataStore).lamps);

    if (this.isProductDetailMode) {
      this.dataStore.productDetailLamps.push(lamp);
    }
  }

  public addLamps(lamps: any[]) {
    lamps.forEach((lamp) => {
      lamp.selected = true;
      this.lampAdded.next(lamp);
    });

    this.dataStore.lamps.push(...lamps);

    this.lamps$.next(Object.assign({}, this.dataStore).lamps);

    if (this.isProductDetailMode) {
      this.dataStore.productDetailLamps.push(...lamps);
    }
  }

  public removeAssignedProduct(lamp: any) {
    lamp.uvpCost = 0;
    lamp.suggestedManufacturer = undefined;
    lamp.suggestedModel = undefined;
    lamp.energySavings = 0;
    lamp.suggestedCharacterNo = undefined;
    lamp.suggestedWatts = undefined;
    lamp.suggestedGuaranteeYears = undefined;
    lamp.costSavings = 0;
    lamp.cO2Savings = 0;
    lamp.suggestedLuminousFlux4000K = undefined;
    lamp.suggestedLuminousFlux3000K = undefined;
    lamp.productId = undefined;
    lamp.isRecommended = false;
    lamp.salesPrice = 0;
    lamp.selected = false;
  }

  public remove(lamp: any) {
    this.dataStore.lamps = remove(this.dataStore.lamps, (storeLamp: any) => {
      lamp.selected = false;

      if (storeLamp.assetId === lamp.assetId) {
        this.removeAssignedProduct(storeLamp);
      }

      return storeLamp.assetId !== lamp.assetId;
    });

    this.lamps$.next(Object.assign({}, this.dataStore).lamps);
    this.lampRemoved.next(lamp);
  }

  public removeLamps(lamps: any[]) {
    const removedLamps: any[] = [];
    lamps.forEach((lamp) => {
      lamp.selected = false;
      this.dataStore.lamps = remove(this.dataStore.lamps, (storeLamp: any) => {
        if (storeLamp.assetId === lamp.assetId) {
          removedLamps.push(storeLamp);
        }
        return storeLamp.assetId !== lamp.assetId;
      });
    });
    removedLamps.forEach((removedLamp) => {
      this.removeAssignedProduct(removedLamp);
      this.lampRemoved.next(removedLamp);
    });
    this.lamps$.next(Object.assign({}, this.dataStore).lamps);
  }

  public resetLamps() {
    this.dataStore.lamps.forEach((lamp: any) => {
      this.removeAssignedProduct(lamp);
    });
    this.dataStore.lamps = [];
    this.lamps$.next([]);
  }

  public update(updatedLamp: any) {
    const lampToBeUpdated = find(this.dataStore.lamps, (lamp) => lamp.assetId === updatedLamp.assetId);
    Object.assign(lampToBeUpdated, updatedLamp);
    this.lamps$.next(Object.assign({}, this.dataStore).lamps);
  }

  public updateLamps(updatedLamps: any[]) {
    updatedLamps.forEach((updatedLamp) => {
      const lampToBeUpdated = find(this.dataStore.lamps, (lamp) => lamp.assetId === updatedLamp.assetId);
      Object.assign(lampToBeUpdated, updatedLamp);
    });

    this.lamps$.next(Object.assign({}, this.dataStore).lamps);
  }

  public resetProductDetailLamps() {
    this.isProductDetailMode = false;
    this.dataStore.productDetailLamps = [];
  }
}
